import { useParams, Link, Navigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Check, ArrowRight, Phone, ArrowLeft, Home, Building2 } from "lucide-react";
import { Seo } from "../../components/public/Seo";
import { QuoteForm } from "../../components/public/QuoteForm";
import { Reviews } from "../../components/public/Reviews";
import { SERVICES, SUBURBS } from "../../data/site";
import { COMPANY } from "../../lib/api";

export default function ServiceDetail() {
  const { slug } = useParams();
  const svc = SERVICES.find((s) => s.slug === slug);
  if (!svc) return <Navigate to="/services" replace />;
  const Icon = svc.icon;

  return (
    <>
      <Seo title={`${svc.name} Perth`} description={`${svc.name} in Perth, WA — ${svc.tagline} West Face Roofing, the roofing specialists. Free quotes, licensed & insured.`} path={`/services/${svc.slug}`} />

      <section className="pt-32 md:pt-44 pb-16 bg-[#1A2421] text-white relative overflow-hidden">
        <img src="https://images.unsplash.com/photo-1587061633437-187ac80e8e7a?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600" alt="" className="absolute inset-0 w-full h-full object-cover opacity-20" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#1A2421] to-transparent" />
        <div className="relative max-w-[1400px] mx-auto px-5 md:px-10">
          <Link to="/services" className="inline-flex items-center gap-2 text-white/60 hover:text-white text-sm font-semibold mb-8"><ArrowLeft size={16} /> All services</Link>
          <div className="grid place-items-center w-16 h-16 rounded-2xl bg-[#62B848] text-[#0c1a12] mb-6"><Icon size={30} /></div>
          <h1 className="font-display font-black tracking-tight text-5xl sm:text-6xl lg:text-7xl leading-none max-w-3xl text-balance">{svc.name}</h1>
          <p className="mt-5 text-white/75 text-xl max-w-xl leading-relaxed">{svc.tagline}</p>
          <div className="mt-6 flex flex-wrap gap-3">
            {svc.forResidential && <span className="inline-flex items-center gap-2 bg-white/10 border border-white/15 rounded-full px-4 py-2 text-sm font-semibold"><Home size={14} className="text-[#62B848]" /> Residential</span>}
            {svc.forCommercial && <span className="inline-flex items-center gap-2 bg-white/10 border border-white/15 rounded-full px-4 py-2 text-sm font-semibold"><Building2 size={14} className="text-[#62B848]" /> Commercial</span>}
          </div>
        </div>
      </section>

      <section className="py-20 md:py-28 bg-[#F8F9FA]">
        <div className="max-w-[1400px] mx-auto px-5 md:px-10 grid lg:grid-cols-[1.4fr_1fr] gap-12">
          <div>
            <h2 className="font-display font-extrabold text-3xl sm:text-4xl text-[#1A2421] leading-tight mb-5">{svc.name} in Perth, done properly</h2>
            <p className="text-[#4A5550] text-lg leading-relaxed mb-8">{svc.blurb} West Face Roofing has been building and maintaining roofs across Perth since {COMPANY.since} — for homeowners, property managers and commercial clients alike.</p>

            <div className="grid sm:grid-cols-2 gap-4 mb-10">
              {svc.points.map((p, i) => (
                <motion.div key={p} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.05 }}
                  className="flex items-start gap-3 bg-white rounded-2xl border border-black/5 p-5">
                  <span className="grid place-items-center w-8 h-8 rounded-xl bg-[#62B848]/15 text-[#62B848] shrink-0"><Check size={16} /></span>
                  <span className="text-[#1A2421] font-medium">{p}</span>
                </motion.div>
              ))}
            </div>

            <div className="bg-white rounded-3xl border border-black/5 p-7">
              <h3 className="font-display font-bold text-xl text-[#1A2421] mb-3">Areas we cover</h3>
              <p className="text-[#4A5550] mb-4">Servicing all of Perth within 20 km of South Perth — residential and commercial. Just outside? Send it through and we'll confirm.</p>
              <Link to="/service-areas" className="inline-flex items-center gap-2 text-[#224733] font-bold hover:gap-3 transition-all">See our service area <ArrowRight size={16} /></Link>
            </div>
          </div>

          <div className="lg:sticky lg:top-28 self-start">
            <QuoteForm defaultJobType={svc.name} />
          </div>
        </div>
      </section>

      <section className="py-12 bg-[#224733]">
        <div className="max-w-[1400px] mx-auto px-5 md:px-10 flex flex-col sm:flex-row items-center justify-between gap-5 text-white">
          <p className="font-display font-bold text-2xl">Need {svc.name.toLowerCase()} sorted today?</p>
          <a href={COMPANY.phoneHref} className="inline-flex items-center gap-2 bg-[#62B848] hover:bg-[#4E9B38] text-white font-bold px-7 py-3.5 rounded-full transition-colors"><Phone size={18} /> {COMPANY.phone}</a>
        </div>
      </section>

      <Reviews compact />
    </>
  );
}
